import React, { useEffect, useState, useCallback } from 'react';
import { Input } from '@material-tailwind/react';
import { debounce } from 'lodash';
import { gql, useQuery } from '@apollo/client';
import { GET_USERS_SEARCH } from '../../../GraphQl/queries';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';

// Define TypeScript types
interface CompanyStaff {
    name: string;
    phone_number: string;
    staff_number: string;
    email: string;
    id: string;
}

interface SubAgent {
    agent_code: string;
    agent_meta_data: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    id: string;
}

interface Broker {
    agent_code: string;
    agent_meta_data: string;
    name: string;
    email: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    id: string;
}

type UserType = 'staff' | 'agent' | 'broker';

type User =
    | (CompanyStaff & { userType: 'staff' })
    | (SubAgent & { userType: 'agent' })
    | (Broker & { userType: 'broker' });


interface UserData {
    company_staff: CompanyStaff[];
    company_agents: SubAgent[];
    brokers: Broker[];
}

interface SearchComponentProps {
    setSelectedIntermediaryId: (id: string) => void;
    setUserType: (userType: string) => void;
    setSelectedUser: (user: { username: string; usercode: string }) => void;
    updateTotalAmount: (val: number) => void;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
    setSelectedIntermediaryId,
    setUserType,
    setSelectedUser,
    updateTotalAmount
}) => {
    const [searchText, setSearchText] = useState('');
    const [filteredResults, setFilteredResults] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { companyId } = useSelector((state: RootState) => state.auth);

    const { loading, data } = useQuery<UserData>(GET_USERS_SEARCH, {
        variables: { id: companyId }
    });

    const handleSearch = useCallback(
        debounce((text: string) => {
            if (!data) return;

            const allUsers: User[] = [
                ...data.company_staff.map(user => ({ ...user, userType: 'staff' } as CompanyStaff & { userType: 'staff' })),
                ...data.company_agents.map(user => ({ ...user, userType: 'agent' } as SubAgent & { userType: 'agent' })),
                ...data.brokers.map(user => ({ ...user, userType: 'broker' } as Broker & { userType: 'broker' }))
            ];


            if (text.trim() === '') {
                setFilteredResults([]);
                setSelectedIntermediaryId('');
                setIsLoading(false);
                return;
            }

            const lowercasedFilter = text.toLowerCase();

            const filtered = allUsers.filter(user => {
                if (user.userType === 'staff') {
                    const staff = user as CompanyStaff;
                    return (
                        staff.name?.toLowerCase().includes(lowercasedFilter) ||
                        staff.phone_number?.toLowerCase().includes(lowercasedFilter) ||
                        staff.staff_number?.toLowerCase().includes(lowercasedFilter) ||
                        staff.email?.toLowerCase().includes(lowercasedFilter)
                    );
                } else if (user.userType === 'agent') {
                    const agent = user as SubAgent;
                    return (
                        (agent.first_name?.toLowerCase().includes(lowercasedFilter) ||
                            agent.last_name?.toLowerCase().includes(lowercasedFilter) ||
                            agent.phone_number?.toLowerCase().includes(lowercasedFilter) ||
                            agent.email?.toLowerCase().includes(lowercasedFilter) ||
                            agent.agent_code?.toLowerCase().includes(lowercasedFilter))
                    );
                } else if (user.userType === 'broker') {
                    const broker = user as Broker;
                    return (
                        broker.name?.toLowerCase().includes(lowercasedFilter) ||
                        broker.phone_number?.toLowerCase().includes(lowercasedFilter) ||
                        broker.email?.toLowerCase().includes(lowercasedFilter)
                    );
                }
            });

            setFilteredResults(filtered);
            setIsLoading(false);
        }, 300),
        [data]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setSearchText(text);
        setIsLoading(true);
        handleSearch(text);
    };

    if (loading) return <div className="text-white">Loading Users...</div>;

    return (
        <div className="w-full bg-white rounded-lg gap-4 p-2">
            <div className="flex space-x-1 w-[40%]">
                <Input
                    crossOrigin="anonymous"
                    label="Search by name, staff_number, email"
                    value={searchText}
                    onChange={handleChange}
                    icon={<i className="fas fa-search text-base"></i>}
                />
                <button
                    onClick={() => handleSearch(searchText)}
                    className="p-2 px-4 bg-black rounded-lg text-white"
                >
                    Search
                </button>
            </div>
            <div>
                {isLoading && <p className="text-sm">Loading...</p>}
                {!isLoading && filteredResults.length === 0 && <p className="text-sm">No results found</p>}
                {!isLoading &&
                    filteredResults.map((user, index) => {
                        const displayName =
                            'name' in user ? user.name : `${user.first_name} ${user.last_name}`;
                        const code =
                            'agent_code' in user
                                ? user.agent_code
                                : 'staff_number' in user
                                    ? user.staff_number
                                    : '';

                        return (
                            <p
                                key={index}
                                className="text-sm cursor-pointer"
                                onClick={() => {
                                    setUserType(user.userType);
                                    setSelectedIntermediaryId(user.id);
                                    updateTotalAmount(0);
                                    localStorage.setItem('int_id', user.id);
                                    setSelectedUser({
                                        username: displayName,
                                        usercode: code
                                    });
                                }}
                            >
                                {displayName} - {code} -{' '}
                                <span className="capitalize">{user.userType}</span>
                            </p>
                        );
                    })}
            </div>
        </div>
    );
};

export default SearchComponent;
